import { Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { theme } from 'antd'
import type { SeedToken } from 'antd/es/theme/interface'
import { useMemo } from 'react'
import { Area, AreaChart, ResponsiveContainer } from 'recharts'
import { useLaunchpadContext } from '../context'

/**
 * Props for the MetricCard component used in InvoiceMetrics
 */
interface MetricCardProps {
  title: string
  value: number
  trend: string
  trendUp?: boolean
  trendDown?: boolean
  count: number
  countLabel: string
  oldest: string
  oldestLabel?: string
  cardStyle?: 'error' | 'warning'
  goodLabel?: string
}

/**
 * Helper functions for styling
 */
const getStrokeColor = (trendDown: boolean | undefined, trendUp: boolean | undefined, token: SeedToken) => {
  if (trendDown) return token.colorSuccess
  if (trendUp) return token.colorError
  return token.colorPrimary
}

const getAppearance = (trendDown: boolean | undefined, trendUp: boolean | undefined) => {
  if (trendDown) return 'success'
  if (trendUp) return 'error'
  return 'default'
}

/**
 * MetricCard component used in InvoiceMetrics for displaying metrics with trends
 */
export function MetricCard({
  title,
  value,
  trend,
  trendUp,
  trendDown,
  count,
  countLabel,
  oldest,
  oldestLabel = 'OLDEST:',
  cardStyle,
  goodLabel,
}: MetricCardProps) {
  const { token } = theme.useToken()
  const { costRevenue } = useLaunchpadContext()

  // Generate mock data with 15 points - using useMemo to avoid recreating on each render
  const data = useMemo(() => {
    return Array.from({ length: 15 }, (_, i) => ({
      value: Math.random() * 10 + (trendUp ? i / 2 : -i / 2),
    }))
  }, [trendUp])

  const strokeColor = getStrokeColor(trendDown, trendUp, token)

  return (
    <Vertical flex={1} bg='default' borderRadius='6px' className='p-3' gap={16}>
      <Horizontal flex='0' style={{ overflow: 'visible' }} justifyContent='space-between' verticalCenter>
        <Vertical gap={8}>
          <Texto category='p2'>{title}</Texto>
          <Horizontal gap={8} verticalCenter>
            <Texto category='h3' weight='bold'>
              {value}
            </Texto>
            <Texto category='p2' weight='600' appearance={getAppearance(trendDown, trendUp)}>
              {trend} <MaterialIcon icon={trendDown ? 'arrow_downward' : 'arrow_upward'} />
            </Texto>
          </Horizontal>
        </Vertical>
        <div className='mr-3' style={{ width: 60, height: 60 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart
              data={data}
              margin={{
                top: 5,
                right: 0,
                left: 0,
                bottom: 5,
              }}
            >
              <defs>
                <linearGradient id={`colorGradient-${strokeColor.replace('#', '')}`} x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={strokeColor} stopOpacity={0.2} />
                  <stop offset='70%' stopColor={strokeColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type='monotone'
                dataKey='value'
                stroke={strokeColor}
                strokeWidth={2}
                fill={`url(#colorGradient-${strokeColor.replace('#', '')})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Horizontal>
      {count === 0 ? (
        <Vertical
          bg='default'
          border='all'
          borderRadius='6px'
          flex='0'
          style={{ overflow: 'visible' }}
          gap={8}
          verticalCenter
          className='py-2 px-3'
        >
          <Horizontal gap={8} verticalCenter>
            <Texto appearance='hint' category='p2'>
              <MaterialIcon
                style={{
                  color: 'var(--theme-success)',
                  verticalAlign: 'middle',
                }}
                icon='family_star'
                fill
                size={24}
              />
            </Texto>
            <Texto weight='bold' category='p2'>
              No {countLabel}
            </Texto>
          </Horizontal>
          <Horizontal gap={8} verticalCenter>
            <Texto category='p2'>{goodLabel}</Texto>
          </Horizontal>
        </Vertical>
      ) : (
        <Vertical
          bg={cardStyle}
          border='all'
          borderRadius='6px'
          flex='0'
          style={{ overflow: 'visible' }}
          gap={8}
          verticalCenter
          className={`py-2 px-3 launchpad-${cardStyle}-card`}
          onClick={() => {
            const today = new Date()
            const thirtyDaysAgo = new Date()
            thirtyDaysAgo.setDate(today.getDate() - 30)

            const fromDate = thirtyDaysAgo.toISOString().split('T')[0]
            const toDate = today.toISOString().split('T')[0]

            window.open(
              `invoices?status=${cardStyle === 'error' ? 'blocked' : 'cr_approval'}&booktype=${costRevenue}&from_date=${fromDate}&to_date=${toDate}`,
              '_blank'
            )
          }}
        >
          <Horizontal gap={8} verticalCenter>
            <Horizontal flex={1} gap={4} verticalCenter>
              <Texto appearance={cardStyle} category='p2'>
                <MaterialIcon className='pt-2' fill icon='error' />
              </Texto>
              <Texto weight='600' category='p2'>
                {count} {countLabel}
              </Texto>
            </Horizontal>
            <Texto category='p2'>→</Texto>
          </Horizontal>
          <Horizontal gap={8} verticalCenter>
            <Texto weight='600' style={{ flex: 1 }} category='p2'>
              {oldestLabel}
            </Texto>
            <Texto category='p2' style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MaterialIcon className='pt-1' icon='schedule' /> {oldest}
            </Texto>
          </Horizontal>
        </Vertical>
      )}
    </Vertical>
  )
}
